import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	useRecoilState,
	useRecoilValue,
	useResetRecoilState,
	useSetRecoilState,
} from 'recoil';

import {
	FundInvestors,
	FundPaginationState,
	FundsState,
	FundsToShowState,
	InviteNavigateModalState,
	IsCreatingFundState,
	IShowFunds,
	OpenInviteSelectorModal,
	PageCountState,
	TempFundListState,
	useFunds,
} from 'global-stores';
import { useTrackEvents } from 'helpers';
import { ROUTES } from 'routes';
import { useFormatNumber } from 'utils';
import { OnboardingInviteModal } from 'views/pipelines/components/onboarding-invite-modal';
import { PERMISSION_SCREEN, useUserRoles } from 'views/routes-children';
import {
	PageHead,
	Pager,
	SearchBox,
	useSv2Theme,
} from 'views/design-system';
import { fundsColumn } from '../constants';
import {
	IFundRow,
	SortKey,
	investorLine,
	pageTotals,
	sortRows,
	toFundRows,
} from './derive';

import 'views/design-system/tokens.scss';
import 'views/design-system/surface.scss';
import './funds-deck.scss';

/** Which underlying key each header sorts on — unchanged from `fundsColumn`. */
const COLUMNS: { label: string; key: SortKey; num?: boolean }[] = [
	{ label: 'Fund / Company', key: 'name' },
	{ label: 'Raised this round', key: 'raised', num: true },
	{ label: 'Total raised', key: 'totalRaised', num: true },
	{ label: 'Post-money valuation', key: 'postMoney', num: true },
];

export const FundsDeck: FC<{ loaded?: boolean }> = () => {
	// Hold the theme claim, so walking in from a dark page does not strobe the
	// shell back to light mid-journey (CLAUDE.md, "Dark mode is claimed").
	useSv2Theme();

	const funds = useRecoilValue(FundsState);
	const fundsStateList = useRecoilValue(TempFundListState);
	const setIsModal = useSetRecoilState(OpenInviteSelectorModal);
	const setNavigation = useSetRecoilState(InviteNavigateModalState);
	const setIsNewFund = useSetRecoilState(IsCreatingFundState);
	const [fundsRow, setFundsRow] = useRecoilState(FundsToShowState);
	const setInvestors = useSetRecoilState(FundInvestors);
	const { numberDecimal } = useFormatNumber();
	const navigate = useNavigate();
	const { trackEvents } = useTrackEvents();
	const { getFunds, isLoaded, pageCounter } = useFunds();
	const { FUNDS } = ROUTES;

	const { checkUserWritePermission } = useUserRoles();
	const pageNumber = useRecoilValue(FundPaginationState);
	const [search, setSearch] = useState('');
	const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({
		key: 'name',
		dir: 1,
	});
	const resetPageCount = useResetRecoilState(PageCountState);
	const restPageNumber = useResetRecoilState(FundPaginationState);

	const isUserPermissionWrite = useMemo(
		() => checkUserWritePermission(PERMISSION_SCREEN.Funds),
		[checkUserWritePermission]
	);

	/**
	 * §5.1.1 — `FundInvestors` is written HERE and read by the detail page. The
	 * detail page derives its own counts now, but this write stays until every
	 * reader has moved, or those counts go to zero.
	 */
	const openFund = useCallback(
		(row: IFundRow) => {
			if (!row.id) return;
			// the atom is typed string (IFundInvestors) and the old list wrote the
			// raw field through — keep the shape, or the detail page's readers break
			setInvestors({
				investors: String(row.investors),
				pendingInvestors: String(row.pending),
			});
			navigate(`${FUNDS}/${row.id}`);
		},
		[FUNDS, navigate, setInvestors]
	);

	/** §5.2 — debounced, so a search is one request rather than one per keystroke. */
	useEffect(() => {
		const t = setTimeout(() => getFunds(search.trim(), !!search), 400);
		return () => clearTimeout(t);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [search]);

	useEffect(() => {
		if (pageNumber !== 1) {
			restPageNumber();
			resetPageCount();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [search]);

	// the row builder the old table needed, kept verbatim so nothing downstream
	// that reads FundsToShowState changes shape
	useEffect(() => {
		const rows: IShowFunds[] = [];
		(funds ?? []).forEach((fund: any) => {
			if (!fund) return;
			let row: any = { id: fund.id };
			fundsColumn.forEach(({ key, key2 }: any) => {
				row = { ...row, [key]: `${fund[key] ?? 0}` };
				if (key === 'moneyRaised') {
					row = { ...row, raised: fund[key] ?? 0, round: fund[key2] ?? 0 };
				}
			});
			rows.push(row);
		});
		setFundsRow(rows);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [funds]);

	const rows = useMemo(() => toFundRows(fundsRow), [fundsRow]);
	const sorted = useMemo(() => sortRows(rows, sort.key, sort.dir), [rows, sort]);
	const totals = useMemo(() => pageTotals(rows), [rows]);

	const handleCreateFund = useCallback(() => {
		trackEvents('create-new-fund', {});
		setIsModal(true);
		setIsNewFund(true);
		setNavigation('create');
	}, [setIsModal, setIsNewFund, setNavigation, trackEvents]);

	const money = useCallback(
		(v: number) => `$${numberDecimal(Number(v || 0))}`,
		[numberDecimal]
	);

	const onSort = (key: SortKey) =>
		setSort(s => ({ key, dir: s.key === key && s.dir === 1 ? -1 : 1 }));

	const isEmpty = isLoaded && !sorted.length;

	return (
		<div className="sv2-scope sv2 funds-deck">
			{/* PageHead renders ThemeToggle itself, so `action` carries only the CTA —
				passing another put two switches side by side in the header. */}
			<PageHead
				title="Funds"
				sub={
					<>
						{rows.length} fund{rows.length === 1 ? '' : 's'} ·{' '}
						{money(totals.raised)} raised across {totals.investors} investor
						{totals.investors === 1 ? '' : 's'} · page {pageNumber} of{' '}
						{Math.max(1, pageCounter || 1)} · reads live from <code>/funds</code>
					</>
				}
				action={
					/* §5.2: permission-gated, and ALWAYS rendered — it used to vanish on
						a search miss, which is exactly when you might want it */
					isUserPermissionWrite ? (
						<button className="cta" type="button" onClick={handleCreateFund}>
							&#65291; Create New
						</button>
					) : undefined
				}
			/>

			<div className="ctl">
				<SearchBox
					value={search}
					onChange={setSearch}
					placeholder="Search funds"
					title="Searches fund and company name"
				/>
				<span className="pgn">
					<Pager
						page={pageNumber}
						pages={Math.max(1, pageCounter || 1)}
						onChange={() => getFunds(search.trim(), !!search)}
					/>
				</span>
			</div>

			<table className="stbl">
				<thead>
					<tr>
						{COLUMNS.map(c => (
							<th
								key={c.key}
								className={c.num ? 'num' : undefined}
								onClick={() => onSort(c.key)}
							>
								{c.label}
								<i className={`crt${sort.key === c.key ? ' on' : ''}`}>
									{sort.key === c.key && sort.dir === -1 ? '▴' : '▾'}
								</i>
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{!isLoaded && (
						<tr>
							<td colSpan={4} className="sv2-loading">
								Loading funds…
							</td>
						</tr>
					)}
					{isEmpty && (
						<tr>
							<td colSpan={4} className="sv2-empty">
								{search
									? `No fund or company matches “${search}”.`
									: 'No funds or companies yet.'}
							</td>
						</tr>
					)}
					{sorted.map(r => (
						<tr key={r.id} onClick={() => openFund(r)}>
							<td className="od">
								<span className="nm">{r.name}</span>
								<div className="fund">{investorLine(r)}</div>
							</td>
							<td className="num">
								<span className="nm">{money(r.raised)}</span>
								{/* §5.2: render nothing where there is nothing, rather than
									a `--` that reads as a value */}
								{r.round && <div className="fund">{r.round}</div>}
							</td>
							<td className="num">
								<span className="nm">{money(r.totalRaised)}</span>
							</td>
							<td className="num">
								{r.postMoney ? (
									<span className="nm">{money(r.postMoney)}</span>
								) : (
									<span className="gy">--</span>
								)}
							</td>
						</tr>
					))}
				</tbody>
			</table>

			<div className="foot">
				Column sorting orders the funds on this page, not the whole list. Click a
				row to open its cap table.
			</div>

			{/* unchanged — the create wizard is `from='funds'` and owns its own state */}
			<OnboardingInviteModal from={'funds'} />
			{fundsStateList ? null : null}
		</div>
	);
};

export default FundsDeck;
